import { useRoute } from 'vue-router';
import type { RouteLocationNormalizedLoaded } from 'vue-router';
import { useArticles } from '@/store/articles';
import { useProducts } from '@/store/products';

// The page/resource a chat message was created from, so it can be linked back to.
export interface ChatContext {
  url: string;
  label: string;
}

const humanize = (value: string): string => value.replace(/[-_]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

// Product detail sub-tab route name → human section word.
const productSections: Record<string, string> = {
  'product-articles': 'Articles',
  'product-bom': 'BOM',
  'product-files': 'Files',
  'product-sampling': 'Sampling',
  'product-mchart': 'Measurement chart',
};

// Best-effort, resource-aware label for the page a message is composed on.
function resolveLabel(route: RouteLocationNormalizedLoaded): string {
  const name = typeof route.name === 'string' ? route.name : '';

  // Product detail + its tabs → "<Product name> <Section>", e.g. "Amelia BOM".
  // The articles tab can also have an article flyout open — surface the article
  // number instead of the bare "Articles" so the link is recognisable.
  if (name === 'product' || name in productSections) {
    const productName = useProducts().loaded[Number(route.params.id)]?.name;
    const section = productSections[name];

    if (name === 'product-articles' && route.params.articleId) {
      const articleNumber = useArticles().getById(Number(route.params.articleId))?.articleNumber;
      if (productName && articleNumber) return `${productName} ${articleNumber}`;
      if (articleNumber) return articleNumber;
    }

    if (productName) return section ? `${productName} ${section}` : productName;
    return section ?? 'Product';
  }

  // Products list with an article flyout open (/plm/products/:articleId) →
  // "Products <Article number>", so the chip is recognisable.
  if (name === 'products' && route.params.articleId) {
    const articleNumber = useArticles().getById(Number(route.params.articleId))?.articleNumber;
    if (articleNumber) return `Products ${articleNumber}`;
  }

  if (name === 'samples') {
    const roundNumber = route.params.roundNumber;
    return typeof roundNumber === 'string' && roundNumber ? `Sample ${roundNumber}` : 'Samples';
  }

  if (name === 'orders') {
    const orderNumber = route.params.orderNumber;
    return typeof orderNumber === 'string' && orderNumber ? `Order ${orderNumber}` : 'Orders';
  }

  const metaTitle = route.meta?.title;
  if (typeof metaTitle === 'string' && metaTitle.length > 0) return metaTitle;

  // Many pages set the browser tab title to the open resource's name (e.g. a
  // product or measurement chart). Use it as a general fallback, stripping the
  // "| Resourced" suffix and ignoring the bare app title.
  const tabTitle = document.title.replace(/\s*\|\s*Resourced\s*$/, '').trim();
  if (tabTitle && tabTitle !== 'Resourced' && tabTitle !== 'Resourced PLM') return tabTitle;

  return name ? humanize(name) : 'this page';
}

// Returns a getter for the current page's chat context. Called at the point a
// message is composed — the URL is always captured, the label is best-effort.
export function useChatContext(): () => ChatContext {
  const route = useRoute();
  return () => ({ url: route.fullPath, label: resolveLabel(route) });
}
